import { useMemo } from 'react';
import type { OUItem } from '@/api/types';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOuScope } from '@/hooks/useOuScope';

export interface AdminOuFilter {
  /** true = actor acotado por OU (Admin OU de Brillamos): el selector NO ofrece «OU: Todas». */
  readonly isOuRestricted: boolean;
  /**
   * Opciones del selector para el actor acotado: las OUs TOPE de su alcance (las que no tienen a su
   * madre dentro del alcance). `null` para el actor sin restricción — cada pantalla usa su catálogo
   * de siempre.
   */
  readonly ouOptions: readonly OUItem[] | null;
  /** OU con la que el selector arranca preseleccionado. `''` = sin preselección (actor global). */
  readonly defaultOuId: string;
  /** true = el selector se pinta deshabilitado: el actor tiene UNA sola OU tope. */
  readonly isLocked: boolean;
  /** El catálogo de OUs todavía no llegó: la preselección aún no se puede decidir. */
  readonly isResolving: boolean;
}

/**
 * Selector de OU de Moderación, Ganadores y Vistas para el Admin de OU (regla de negocio del
 * 2026-09-23, antes aplicaba solo a Reportería): sin la opción «OU: Todas», preseleccionado con la
 * OU del administrador y bloqueado.
 *
 * 🔴 `recognitionOuScope.rootOuIds` llega EXPANDIDO desde el backend (ver `AdminCampaigns.tsx`), así
 * que «la OU del usuario» no es `rootOuIds[0]`: es el tope de su alcance, las OUs cuya madre queda
 * FUERA del alcance. Con un solo tope (el caso normal: FOSE, México…) se preselecciona y se bloquea.
 * Con varios topes se quita «Todas» y se preselecciona uno —el de su OU de adscripción si es uno de
 * ellos—, pero el selector queda editable entre SUS OUs: bloquearlo escondería el resto de su alcance.
 *
 * Es capa de UX: el backend ya acota los RESULTADOS por alcance en las tres pantallas.
 */
export function useAdminOuFilter(): AdminOuFilter {
  const currentUser = useCurrentUser();
  const { isUnrestricted, allowedOuIds, scopedOuItems, isResolving } = useOuScope('brillamos');

  const ouOptions = useMemo<OUItem[] | null>(() => {
    if (isUnrestricted || allowedOuIds === null) return null;
    return scopedOuItems.filter((ou) => !ou.parentOuId || !allowedOuIds.has(ou.parentOuId));
  }, [isUnrestricted, allowedOuIds, scopedOuItems]);

  const ownOuId = currentUser?.ouId;
  const defaultOuId = useMemo(() => {
    if (!ouOptions || ouOptions.length === 0) return '';
    return ouOptions.find((ou) => ou.id === ownOuId)?.id ?? ouOptions[0].id;
  }, [ouOptions, ownOuId]);

  return {
    isOuRestricted: ouOptions !== null,
    ouOptions,
    defaultOuId,
    isLocked: ouOptions !== null && ouOptions.length === 1,
    isResolving,
  };
}
